<template>
    <v-row class="mx-3">
      <v-col>
        <v-card>
          <v-card-title class="d-flex align-center">
            {{ $t('profile.referee') }}
            <v-spacer/>
            <v-icon color="green" size="40" :title="$t('classes.create_referee')" v-if="isManager"
                    @click="createOrEditRole('referee',true,null)">
              mdi-plus
            </v-icon>
          </v-card-title>
          <v-data-table
              density="compact"
              hide-default-footer
              :headers="roleHeaders"
              :items="rolesReferee"
              class="elevation-1"
              :items-per-page="-1"
              :sort-by="roleSortBy"
              :loading="loading"
              loading-text="general.loading"
              no-data-text="general.no_data"
          >
            <template v-slot:[`item.lvl`]="{ item }">
              {{ toRomanLevel(item.lvl) }}.
            </template>
            <template v-slot:[`item.valid_from`]="{ item }">
              {{ formatDate(item.valid_from) }}
            </template>
            <template v-slot:[`item.valid_to_sort`]="{ item }">
              {{ formatValidTo(item.valid_to) }}
            </template>
            <template v-slot:[`item.created_by`]="{ item }">
              {{ item.created_by }}
            </template>
            <template v-slot:[`item.description`]="{ item }">
              <span style="white-space: nowrap;">{{ item.description }}</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }" v-if="isManager">
              <v-list-item-action>
                <v-icon @click="createOrEditRole('referee',false,item)" color="blue" size="30" class="mr-2"
                        :title="$t('classes.edit_role')">mdi-pencil
                </v-icon>
                <v-icon @click="openDeleteDialog(item)" color="red" size="30" class="mr-2"
                        :title="$t('classes.delete_role')">mdi-delete
                </v-icon>
              </v-list-item-action>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col>
        <v-card>
          <v-card-title class="d-flex align-center">
            {{ $t('profile.coach') }}
            <v-spacer/>
            <v-icon color="green" size="40" :title="$t('classes.create_coach')" v-if="isManager"
                    @click="createOrEditRole('coach',true,null)">mdi-plus
            </v-icon>
          </v-card-title>

          <v-data-table
              density="compact"
              hide-default-footer
              :headers="roleHeaders"
              :items="rolesCoach"
              class="elevation-1"
              :items-per-page="-1"
              :sort-by="roleSortBy"
              :loading="loading"
              loading-text="general.loading"
              no-data-text="general.no_data"
          >
            <template v-slot:[`item.lvl`]="{ item }">
              {{ toRomanLevel(item.lvl) }}.
            </template>
            <template v-slot:[`item.valid_from`]="{ item }">
              {{ formatDate(item.valid_from) }}
            </template>
            <template v-slot:[`item.valid_to_sort`]="{ item }">
              {{ formatValidTo(item.valid_to) }}
            </template>
            <template v-slot:[`item.created_by`]="{ item }">
              {{ item.created_by }}
            </template>
            <template v-slot:[`item.description`]="{ item }">
              <span style="white-space: nowrap;">{{ item.description }}</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }" v-if="isManager">
              <v-list-item-action>
                <v-icon @click="createOrEditRole('coach',false,item)" color="blue" size="30" class="mr-2"
                        :title="$t('classes.edit_role')">mdi-pencil
                </v-icon>
                <v-icon @click="openDeleteDialog(item)" color="red" size="30" class="mr-2" :title="$t('classes.delete_role')">
                  mdi-delete
                </v-icon>
              </v-list-item-action>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>


  <TheRoleEditDialog
      v-model="roleDialogOpen"
      :dialog="roleDialogOpen"
      :role-type="selectedRoleType"
      :create="create"
      :account-id="props.accountId"
      :role="selectedRole"
      :excluded-role-levels="excludedRoleLevels"
      @role-saved="updateRoles(true)"
      @close-dialog="roleDialogOpen=false"
  />

  <!-- Dialog pro potvrzení smazání role -->
  <v-dialog v-model="dialogDeleteRole" persistent max-width="600px">
    <v-card>
      <v-card-title class="text-h5">{{ $t('classes.delete_dialog_title') }}</v-card-title>
      <v-card-text>
        <v-alert type="error" v-if="errorMessage">{{ errorMessage }}</v-alert>
        {{ $t('classes.delete_dialog_text') }}
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="dialogDeleteRole = false">{{ $t('general.cancel') }}</v-btn>
        <v-btn color="red darken-1" text @click="deleteRole">{{ $t('classes.delete_class_btn') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>

</style>

<script setup>

import {onMounted, ref, computed} from "vue";
import apiClient from '/api.js';
import {useAuthStore} from '/auth.js';
import TheRoleEditDialog from "@/components/TheRoleEditDialog.vue";
import {useSnackbarStore} from '@/stores/snackbarStore'
import {useI18n} from 'vue-i18n'
import { useDateTimeFormat } from '@/composables/useDateTimeFormat.js'


const { t } = useI18n()
const authStore = useAuthStore();
const snackbar = useSnackbarStore()
const { formatDate } = useDateTimeFormat()



// eslint-disable-next-line no-undef
const props = defineProps({
  accountId: Number
})

const loading = ref(true);
const errorMessage = ref("")

const rolesReferee = ref([]);
const rolesCoach = ref([]);

// Proměnné pro vytváření / editaci rolí
const create = ref(false);
const roleDialogOpen = ref(false);
const selectedRole = ref(null);
const selectedRoleType = ref(null);

const isManager = authStore.isManager(); // Načtení práv
const roleSortBy = [{key: 'valid_to_sort', order: 'desc'}];

const roleHeaders = [
  {title: t('profile.class'), key: 'lvl', align: 'left', sortable: false},
  {title: t('profile.valid_from'), key: 'valid_from', align: 'left', sortable: false},
  {title: t('profile.valid_to'), key: 'valid_to_sort', align: 'left', sortable: true},
  {title: t('profile.created'), key: 'created_by', align: 'left', sortable: false},
  {title: t('profile.note'), key: 'description', align: 'left', sortable: false},
  ...(isManager ? [{title: t('profile.actions'), value: 'actions', align: 'center', width: '30px', sortable: false}] : []), 
];


const toRomanLevel = (lvl) => {
  const v = String(lvl ?? '').trim()
  if (v === '1') return 'I'
  if (v === '2') return 'II'
  if (v === '3') return 'III'
  return v
}


const addValidToSort = (r) => ({
  ...r,
  valid_to_sort: r.valid_to ? Date.parse(r.valid_to) : Infinity,
})

// Načítání rolí uživatele
const loadRoles = async () => {
  try {
    const response = await apiClient.get(`account/role/?account_id=${props.accountId}`);
    rolesReferee.value = response.data["referees"].map(addValidToSort);
    rolesCoach.value = response.data["coaches"].map(addValidToSort);
  } catch (error) {
    console.error(`There was an error fetching the roles for accountId (${props.accountId}):`, error);
  } finally {
    loading.value = false
  }
}

// formátování data valid_to
const isInvalidFormattedDate = (formatted) => {
  if (!formatted) return true
  const s = String(formatted).toLowerCase()
  return s.includes('neplatn') || s.includes('není znám') || s.includes('n/a')
}

const formatValidTo = (value) => {
  const formatted = formatDate(value)
  return isInvalidFormattedDate(formatted) ? '∞' : formatted
}

const excludedRoleLevels = computed(() => {
  const now = new Date();
  if (selectedRoleType.value === 'referee') {
    const activeRole = rolesReferee.value.find(r => new Date(r.valid_to) > now);
    return activeRole ? [activeRole.lvl] : [];
  } else if (selectedRoleType.value === 'coach') {
    const activeRole = rolesCoach.value.find(r => !r.valid_to || new Date(r.valid_to) > now);
    return activeRole ? [activeRole.lvl] : [];
  }
  return [];
});


const createOrEditRole = (roleType, isCreated, role) => {
  roleDialogOpen.value = true;
  selectedRoleType.value = roleType;
  create.value = isCreated
  selectedRole.value = role;
};

const dialogDeleteRole = ref(false);
const openDeleteDialog = (role) => {
  selectedRole.value = role;
  errorMessage.value = "";
  dialogDeleteRole.value = true;
};

const updateRoles = (showMessage) => {
  if (showMessage) {
    errorMessage.value = "";
    snackbar.success(t('classes.save_success'));
  }

  roleDialogOpen.value = false;
  loadRoles();
}

const deleteRole = async () => {
  try {

    // Odeslání požadavku DELETE na API
    await apiClient.delete(`account/role/?role_id=${selectedRole.value.role_id}`, {});

    snackbar.success(t('classes.delete_success'));
    errorMessage.value = "";
    dialogDeleteRole.value = false; // Zavření potvrzovacího dialogu
    updateRoles(false)
  } catch (error) {
    const detail = error.response.data.detail || 'Unknown error';
    console.error('Chyba při mazání třídy:', error.message);
    errorMessage.value = `${t('classes.delete_failed')} ${detail}`;
  }
};

onMounted(() => {
  loadRoles(props.accountId)
});

</script>